<div class="space-y-8">
    <ng-container [ngSwitch]="currentStep">
        <!-- ═══════════════ Step 1 · Property Information ═══════════════ -->
        <form *ngSwitchCase="1" [formGroup]="propertyForm" (ngSubmit)="nextStep()" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 1 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Property Information</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Basic details</span>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Title *</span>
                    <input formControlName="title" type="text" class="field" [ngClass]="inputClasses('title')" placeholder="Luxury Beachfront Villa" />
                    <span *ngIf="fieldInvalid('title')" class="block text-xs text-[color:var(--destructive)]">Title is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Tagline</span>
                    <input formControlName="tagLine" type="text" class="field" placeholder="Your dream home awaits" />
                </label>
            </div>

            <label class="block space-y-1.5">
                <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Description</span>
                <textarea formControlName="description" rows="4" class="field resize-y" placeholder="Stunning 5-bedroom villa with panoramic ocean views..."></textarea>
            </label>

            <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                <div>
                    <p class="text-sm font-semibold text-[color:var(--foreground)]">Offer assets</p>
                    <p class="text-xs text-[color:var(--muted-foreground)]">Required for generating per-lead offers (PDF). These files must be public so they can be rendered in offer PDFs.</p>
                </div>

                <div class="grid gap-6 md:grid-cols-2 md:divide-x md:divide-[color:var(--border)]">
                    <div class="space-y-3 md:pr-6">
                        <div class="flex items-start justify-between gap-2">
                            <div>
                                <p class="text-sm font-medium text-[color:var(--foreground)]">Floor plan image</p>
                                <p class="text-xs text-[color:var(--text-subtle)]">Required for offers.</p>
                            </div>
                            <button *ngIf="floorPlanFile" type="button" (click)="clearFloorPlan()" class="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border)] px-2.5 py-1.5 text-xs font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--destructive)]">
                                <lucide-icon name="trash-2" class="h-3.5 w-3.5"></lucide-icon>
                                Remove
                            </button>
                        </div>
                        <label class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                            <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                            <span>{{ floorPlanFile ? 'Replace' : 'Upload' }}</span>
                            <input type="file" class="hidden" accept="image/*" (change)="onFloorPlanSelected($event)" />
                        </label>
                        <div class="flex items-center gap-2 text-xs text-[color:var(--muted-foreground)]">
                            <lucide-icon name="file-image" class="h-4 w-4 shrink-0 text-[color:var(--text-subtle)]"></lucide-icon>
                            <span class="truncate max-w-[18rem]">{{ floorPlanFile?.name || 'No floor plan selected' }}</span>
                        </div>
                        <p *ngIf="floorPlanError" class="text-xs text-[color:var(--destructive)]">{{ floorPlanError }}</p>
                    </div>

                    <div class="space-y-3 md:pl-6">
                        <div class="flex items-start justify-between gap-2">
                            <div>
                                <p class="text-sm font-medium text-[color:var(--foreground)]">Layout image</p>
                                <p class="text-xs text-[color:var(--text-subtle)]">Optional.</p>
                            </div>
                            <button *ngIf="layoutImageFile" type="button" (click)="clearLayoutImage()" class="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border)] px-2.5 py-1.5 text-xs font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--destructive)]">
                                <lucide-icon name="trash-2" class="h-3.5 w-3.5"></lucide-icon>
                                Remove
                            </button>
                        </div>
                        <label class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                            <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                            <span>{{ layoutImageFile ? 'Replace' : 'Upload' }}</span>
                            <input type="file" class="hidden" accept="image/*" (change)="onLayoutImageSelected($event)" />
                        </label>
                        <div class="flex items-center gap-2 text-xs text-[color:var(--muted-foreground)]">
                            <lucide-icon name="file-image" class="h-4 w-4 shrink-0 text-[color:var(--text-subtle)]"></lucide-icon>
                            <span class="truncate max-w-[18rem]">{{ layoutImageFile?.name || 'No layout image selected' }}</span>
                        </div>
                        <p *ngIf="layoutImageError" class="text-xs text-[color:var(--destructive)]">{{ layoutImageError }}</p>
                    </div>
                </div>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Project *</span>
                    <select formControlName="projectId" (change)="onProjectChange()" class="field" [ngClass]="inputClasses('projectId')">
                        <option value="">Select project</option>
                        <option *ngFor="let project of projects" [value]="project.id">{{ project.name }}</option>
                    </select>
                    <span *ngIf="fieldInvalid('projectId')" class="block text-xs text-[color:var(--destructive)]">Project is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Phase *</span>
                    <select formControlName="phaseId" class="field" [ngClass]="inputClasses('phaseId')">
                        <option value="">Select phase</option>
                        <option *ngFor="let phase of phases" [value]="phase.id">{{ phase.name }}</option>
                    </select>
                    <span *ngIf="fieldInvalid('phaseId')" class="block text-xs text-[color:var(--destructive)]">Phase is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Country *</span>
                    <div [ngClass]="countryClasses()">
                        <app-country-selector formControlName="country"></app-country-selector>
                    </div>
                    <span *ngIf="fieldInvalid('country')" class="block text-xs text-[color:var(--destructive)]">Country is required.</span>
                </label>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Status *</span>
                    <select formControlName="status" class="field" [ngClass]="inputClasses('status')">
                        <option value="Available">Available</option>
                        <option value="ReservedOrHold">Reserved / Hold</option>
                        <option value="ReservedWithDeposit">Reserved with deposit</option>
                        <option value="Sold">Sold</option>
                        <option value="Contracted">Contracted</option>
                        <option value="PendingApproval">Pending Approval</option>
                        <option value="Unpublished">Unpublished</option>
                    </select>
                    <span class="block text-[11px] text-[color:var(--text-subtle)]">You can use this unit right away. It appears on the public website only after nibni publishes it.</span>
                    <span *ngIf="fieldInvalid('status')" class="block text-xs text-[color:var(--destructive)]">Status is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Price *</span>
                    <input formControlName="price" type="number" class="field" [ngClass]="inputClasses('price')" placeholder="1050000" />
                    <span *ngIf="fieldInvalid('price')" class="block text-xs text-[color:var(--destructive)]">Price is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Currency *</span>
                    <select formControlName="currencyId" class="field" [ngClass]="inputClasses('currencyId')">
                        <option *ngFor="let currency of currencies" [value]="currency.ROWID">{{ currency.CODE || currency.NAME }}</option>
                    </select>
                    <span *ngIf="fieldInvalid('currencyId')" class="block text-xs text-[color:var(--destructive)]">Currency is required.</span>
                </label>
            </div>

            <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Built-up Area (BUA) *</span>
                    <div class="flex items-center gap-2">
                        <input formControlName="bua" type="number" class="field" [ngClass]="inputClasses('bua')" placeholder="280.5" />
                        <select formControlName="buaUnit" class="field !w-20 shrink-0">
                            <option *ngFor="let unit of areaUnits" [value]="unit">{{ unit }}</option>
                        </select>
                    </div>
                    <span *ngIf="fieldInvalid('bua')" class="block text-xs text-[color:var(--destructive)]">BUA is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">
                        Total Area
                        <span class="flex items-center gap-1.5 text-[11px] font-medium normal-case tracking-normal text-[color:var(--muted-foreground)]">
                            <input type="checkbox" class="accent-[color:var(--primary)]" formControlName="totalAreaEnabled" (change)="onAreaToggle('totalAreaEnabled','totalArea')" />
                            Enable
                        </span>
                    </span>
                    <div class="flex items-center gap-2">
                        <input formControlName="totalArea" type="number" class="field" placeholder="300.5" [disabled]="!propertyForm.get('totalAreaEnabled')?.value" />
                        <select formControlName="totalAreaUnit" class="field !w-20 shrink-0">
                            <option *ngFor="let unit of areaUnits" [value]="unit">{{ unit }}</option>
                        </select>
                    </div>
                </label>
                <label class="block space-y-1.5">
                    <span class="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">
                        Garden Area
                        <span class="flex items-center gap-1.5 text-[11px] font-medium normal-case tracking-normal text-[color:var(--muted-foreground)]">
                            <input type="checkbox" class="accent-[color:var(--primary)]" formControlName="gardenAreaEnabled" (change)="onAreaToggle('gardenAreaEnabled','gardenArea')" />
                            Enable
                        </span>
                    </span>
                    <div class="flex items-center gap-2">
                        <input formControlName="gardenArea" type="number" class="field" placeholder="50" [disabled]="!propertyForm.get('gardenAreaEnabled')?.value" />
                        <select formControlName="gardenAreaUnit" class="field !w-20 shrink-0">
                            <option *ngFor="let unit of areaUnits" [value]="unit">{{ unit }}</option>
                        </select>
                    </div>
                </label>
                <label class="block space-y-1.5">
                    <span class="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">
                        Balcony Area
                        <span class="flex items-center gap-1.5 text-[11px] font-medium normal-case tracking-normal text-[color:var(--muted-foreground)]">
                            <input type="checkbox" class="accent-[color:var(--primary)]" formControlName="balconyAreaEnabled" (change)="onAreaToggle('balconyAreaEnabled','balconyArea')" />
                            Enable
                        </span>
                    </span>
                    <div class="flex items-center gap-2">
                        <input formControlName="balconyArea" type="number" class="field" placeholder="30" [disabled]="!propertyForm.get('balconyAreaEnabled')?.value" />
                        <select formControlName="balconyAreaUnit" class="field !w-20 shrink-0">
                            <option *ngFor="let unit of areaUnits" [value]="unit">{{ unit }}</option>
                        </select>
                    </div>
                </label>
                <label class="block space-y-1.5">
                    <span class="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">
                        Terrace Area
                        <span class="flex items-center gap-1.5 text-[11px] font-medium normal-case tracking-normal text-[color:var(--muted-foreground)]">
                            <input type="checkbox" class="accent-[color:var(--primary)]" formControlName="terraceAreaEnabled" (change)="onAreaToggle('terraceAreaEnabled','terraceArea')" />
                            Enable
                        </span>
                    </span>
                    <div class="flex items-center gap-2">
                        <input formControlName="terraceArea" type="number" class="field" placeholder="30" [disabled]="!propertyForm.get('terraceAreaEnabled')?.value" />
                        <select formControlName="terraceAreaUnit" class="field !w-20 shrink-0">
                            <option *ngFor="let unit of areaUnits" [value]="unit">{{ unit }}</option>
                        </select>
                    </div>
                </label>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Bedrooms</span>
                    <select formControlName="bedrooms" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of bedroomOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Bathrooms</span>
                    <select formControlName="bathrooms" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of bedroomOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Number of Floors</span>
                    <select formControlName="numberOfFloors" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of numberOfFloorsSelectOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Floor</span>
                    <select formControlName="floor" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of floorSelectOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Unit Number{{ unitNumberRequired ? ' *' : '' }}</span>
                    <input formControlName="unitNumber" type="text" class="field" [ngClass]="inputClasses('unitNumber')" placeholder="B-302" />
                    <span *ngIf="fieldInvalid('unitNumber')" class="block text-xs text-[color:var(--destructive)]">Unit number is required.</span>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Ready to Move</span>
                    <select formControlName="readyToMove" class="field">
                        <option value="">Select</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Finishing Type</span>
                    <select formControlName="finishingType" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of finishingOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Furnish Type</span>
                    <select formControlName="furnishType" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of furnishOptions" [value]="option">{{ option }}</option>
                    </select>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Discount Rate (%)</span>
                    <select formControlName="discountRateChoice" class="field">
                        <option value="">Select</option>
                        <option *ngFor="let option of discountOptions" [value]="option">
                            {{ option === 'custom' ? 'Custom' : option + '%' }}
                        </option>
                    </select>
                    <input *ngIf="propertyForm.get('discountRateChoice')?.value === 'custom'" formControlName="customDiscountRate" type="number" min="0" max="100" step="0.01" class="field mt-2" placeholder="Enter custom rate" />
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Maintenance Fees</span>
                    <div class="flex items-center gap-2">
                        <input formControlName="maintenanceFees" type="number" class="field" placeholder="1000" />
                        <select formControlName="maintenanceFeeMode" class="field !w-36 shrink-0">
                            <option *ngFor="let mode of maintenanceModes" [value]="mode.value">{{ mode.label }}</option>
                        </select>
                    </div>
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Delivery Date</span>
                    <input formControlName="deliveryDate" type="date" class="field" />
                </label>
                <label class="block space-y-1.5">
                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Property Type *</span>
                    <select formControlName="categoryId" class="field" [ngClass]="inputClasses('categoryId')">
                        <option *ngFor="let opt of propertyTypeSelectOptions" [value]="opt.value" [disabled]="opt.disabled">{{ opt.label }}</option>
                    </select>
                    <span *ngIf="fieldInvalid('categoryId')" class="block text-xs text-[color:var(--destructive)]">Property type is required.</span>
                </label>
            </div>

            <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm font-semibold text-[color:var(--foreground)]">Location</p>
                        <p class="text-xs text-[color:var(--muted-foreground)]">By default, properties inherit the project location.</p>
                    </div>
                    <div class="inline-flex rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] p-1">
                        <button
                            type="button"
                            (click)="setUseProjectLocation(true)"
                            class="rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
                            [ngClass]="useProjectLocation ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'"
                        >
                            Project
                        </button>
                        <button
                            type="button"
                            (click)="setUseProjectLocation(false)"
                            class="rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
                            [ngClass]="!useProjectLocation ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'"
                        >
                            Custom
                        </button>
                    </div>
                </div>

                <ng-container *ngIf="useProjectLocation; else customLocation">
                    <div class="grid gap-3 md:grid-cols-2">
                        <div class="rounded-lg bg-[color:var(--muted)] p-3">
                            <p class="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Location Address</p>
                            <p class="mt-1 text-sm text-[color:var(--foreground)]">{{ projectLocationAddress || '—' }}</p>
                        </div>
                        <div class="rounded-lg bg-[color:var(--muted)] p-3">
                            <p class="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Location Link</p>
                            <a
                                *ngIf="projectLocationLink"
                                [href]="projectLocationLink"
                                target="_blank"
                                rel="noreferrer"
                                class="mt-1 inline-flex items-center gap-1.5 text-sm text-[color:var(--primary)] hover:underline"
                            >
                                <lucide-icon name="external-link" class="h-3.5 w-3.5"></lucide-icon>
                                Open map
                            </a>
                            <p *ngIf="!projectLocationLink" class="mt-1 text-sm text-[color:var(--text-subtle)]">—</p>
                        </div>
                    </div>
                    <p class="text-xs text-[color:var(--text-subtle)]">To override, switch to Custom.</p>
                </ng-container>

                <ng-template #customLocation>
                    <div class="grid gap-4 md:grid-cols-2">
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Location Address</span>
                            <input formControlName="locationAddress" type="text" class="field" placeholder="e.g. New Cairo, Fifth Settlement" />
                            <span class="block text-[11px] text-[color:var(--text-subtle)]">This will be shown on the property card instead of the auto-generated location string.</span>
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Location Link (Google Maps)</span>
                            <input formControlName="locationLink" type="url" class="field" placeholder="https://maps.google.com/..." />
                            <span class="block text-[11px] text-[color:var(--text-subtle)]">Must start with http(s)://</span>
                        </label>
                    </div>
                </ng-template>
            </div>

            <div class="flex items-center justify-between gap-3">
                <p class="hidden text-xs text-[color:var(--text-subtle)] sm:block">Required — Title, Project, Phase, Country, Status, BUA</p>
                <button type="submit" [disabled]="saving" class="ml-auto inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <span>Continue</span>
                    <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
                </button>
            </div>
            <p *ngIf="error" class="text-sm text-[color:var(--destructive)]">{{ error }}</p>
        </form>

        <!-- ═══════════════ Step 2 · Features ═══════════════ -->
        <div *ngSwitchCase="2" [formGroup]="propertyForm" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 2 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Features</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Property features</span>
            </div>

            <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                <div>
                    <p class="text-sm font-semibold text-[color:var(--foreground)]">Unit badges</p>
                    <p class="text-xs text-[color:var(--muted-foreground)]">The project development badge stays first. Add up to two more top-right badges for the unit card.</p>
                </div>

                <div class="grid gap-4 lg:grid-cols-2">
                    <label class="block space-y-1.5">
                        <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Unit badge type</span>
                        <select
                            [value]="unitBadgePrimaryValue"
                            (change)="onUnitBadgePrimaryChange(($any($event.target).value || '').toString())"
                            class="field"
                        >
                            <option value="">No unit badge type</option>
                            <option *ngFor="let option of unitBadgePrimaryOptions" [value]="option">{{ option }}</option>
                        </select>
                    </label>
                    <div class="space-y-1.5">
                        <span class="block text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Unit badge options</span>
                        <div class="grid gap-2 sm:grid-cols-3">
                            <label
                                *ngFor="let option of unitBadgeSecondaryOptions"
                                class="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors"
                                [ngClass]="isUnitSecondaryBadgeSelected(option)
                                    ? 'border-[color:var(--primary)] bg-[color:var(--primary)]/10 text-[color:var(--foreground)]'
                                    : 'border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'"
                                [class.opacity-50]="isUnitSecondaryBadgeDisabled(option)"
                            >
                                <input
                                    type="checkbox"
                                    class="accent-[color:var(--primary)]"
                                    [checked]="isUnitSecondaryBadgeSelected(option)"
                                    [disabled]="isUnitSecondaryBadgeDisabled(option)"
                                    (change)="onUnitSecondaryBadgeToggle(option, $any($event.target).checked)"
                                />
                                <span>{{ option }}</span>
                            </label>
                        </div>
                        <p class="text-[11px] text-[color:var(--text-subtle)]">
                            Choose up to {{ unitBadgeSecondaryLimit }} option{{ unitBadgeSecondaryLimit === 1 ? '' : 's' }} here.
                        </p>
                    </div>
                </div>
            </div>

            <div class="grid gap-3 sm:grid-cols-2">
                <button type="button"
                        *ngFor="let feature of availableFeatures"
                        (click)="toggleFeature(feature)"
                        [attr.aria-pressed]="isFeatureSelected(feature.id)"
                        class="flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ring)]"
                        [ngClass]="{
                            'border-[color:var(--primary)] bg-[color:var(--primary)]/10 text-[color:var(--foreground)]': isFeatureSelected(feature.id),
                            'border-[color:var(--border)] bg-[color:var(--card)] text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]': !isFeatureSelected(feature.id)
                        }">
                    <div>
                        <p class="text-sm font-semibold">{{ feature.name }}</p>
                    </div>
                    <lucide-icon *ngIf="isFeatureSelected(feature.id)" name="check" class="h-4 w-4 text-[color:var(--primary)]"></lucide-icon>
                </button>
            </div>

            <div class="flex items-center justify-between gap-3">
                <button type="button" (click)="prevStep()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="arrow-left" class="h-4 w-4"></lucide-icon>
                    Back
                </button>
                <button type="button" (click)="nextStep()" [disabled]="saving" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <span>Continue</span>
                    <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
                </button>
            </div>
            <p *ngIf="error" class="text-sm text-[color:var(--destructive)]">{{ error }}</p>
        </div>

        <!-- ═══════════════ Step 3 · Landmarks ═══════════════ -->
        <form *ngSwitchCase="3" [formGroup]="landmarksForm" (ngSubmit)="nextStep()" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 3 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Nearby Landmarks</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Points of interest</span>
            </div>

            <div formArrayName="items" class="space-y-4">
                <div *ngFor="let group of landmarks.controls; let i = index" [formGroupName]="i" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                    <div class="flex items-center justify-between">
                        <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Landmark {{ i + 1 }}</p>
                        <button type="button" (click)="removeLandmark(i)" [disabled]="landmarks.length === 1" class="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border)] px-2.5 py-1.5 text-xs font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--destructive)] disabled:opacity-40">
                            <lucide-icon name="trash-2" class="h-3.5 w-3.5"></lucide-icon>
                            Remove
                        </button>
                    </div>
                    <div class="grid gap-4 md:grid-cols-4">
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Preset</span>
                            <select formControlName="type" (change)="applyLandmarkPreset(i)" class="field">
                                <option value="custom">Custom</option>
                                <option *ngFor="let preset of landmarkPresets" [value]="preset">{{ preset }}</option>
                            </select>
                        </label>
                        <label *ngIf="group.get('type')?.value === 'custom'" class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Name</span>
                            <input formControlName="name" type="text" class="field" placeholder="Metro Station" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Distance</span>
                            <div class="flex items-center gap-2">
                                <input formControlName="distance" type="number" class="field" placeholder="800" />
                                <select formControlName="distanceUnit" class="field !w-20 shrink-0">
                                    <option *ngFor="let unit of distanceUnits" [value]="unit.value">{{ unit.label }}</option>
                                </select>
                            </div>
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Time (min)</span>
                            <input formControlName="timeMinutes" type="number" class="field" placeholder="10" />
                        </label>
                    </div>
                </div>

                <button type="button" (click)="addLandmark()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
                    Add landmark
                </button>
            </div>

            <div class="flex items-center justify-between gap-3">
                <button type="button" (click)="prevStep()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="arrow-left" class="h-4 w-4"></lucide-icon>
                    Back
                </button>
                <button type="submit" [disabled]="saving" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <span>Continue</span>
                    <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
                </button>
            </div>
            <p *ngIf="error" class="text-sm text-[color:var(--destructive)]">{{ error }}</p>
        </form>

        <!-- ═══════════════ Step 4 · Images ═══════════════ -->
        <div *ngSwitchCase="4" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 4 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Property Images</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Upload photos</span>
            </div>

            <div class="rounded-xl border border-dashed border-[color:var(--border)] bg-[color:var(--card)] p-8 text-center">
                <lucide-icon name="image" class="mx-auto mb-3 h-10 w-10 text-[color:var(--text-subtle)]"></lucide-icon>
                <p class="font-semibold text-[color:var(--foreground)]">Upload property photos</p>
                <p class="mb-4 text-sm text-[color:var(--muted-foreground)]">JPG or PNG</p>
                <div class="flex flex-wrap items-center justify-center gap-3">
                    <label class="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
                        <lucide-icon name="upload" class="h-4 w-4"></lucide-icon>
                        <span>Select images</span>
                        <input type="file" class="hidden" multiple accept="image/*" (change)="onImagesSelected($event)" />
                    </label>
                </div>
            </div>

            <div *ngIf="images.length" class="grid gap-4 md:grid-cols-2">
                <div *ngFor="let img of images; let i = index" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4">
                    <div class="flex items-center gap-4">
                        <img [src]="img.preview" alt="Uploaded preview" class="h-20 w-24 rounded-lg border border-[color:var(--border)] object-cover" />
                        <div class="flex-1 space-y-2">
                            <input [(ngModel)]="img.description" placeholder="Description" class="field" />
                            <div class="flex items-center gap-2">
                                <input type="radio" class="accent-[color:var(--primary)]" [checked]="img.isMain" (change)="setMainImage(i)" />
                                <span class="text-sm text-[color:var(--muted-foreground)]">Set as main</span>
                            </div>
                        </div>
                        <button type="button" (click)="removeImage(i)" class="rounded-lg border border-[color:var(--border)] p-2 text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--destructive)]">
                            <lucide-icon name="trash-2" class="h-4 w-4"></lucide-icon>
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex items-center justify-between gap-3">
                <button type="button" (click)="prevStep()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="arrow-left" class="h-4 w-4"></lucide-icon>
                    Back
                </button>
                <button type="button" (click)="nextStep()" [disabled]="saving" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <span>Continue</span>
                    <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
                </button>
            </div>
            <p *ngIf="error" class="text-sm text-[color:var(--destructive)]">{{ error }}</p>
        </div>

        <!-- ═══════════════ Step 5 · Payment Plans ═══════════════ -->
        <form *ngSwitchCase="5" [formGroup]="paymentPlansForm" (ngSubmit)="nextStep()" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 5 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Payment Plans</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Financing options</span>
            </div>

            <div class="rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] p-3 text-sm text-[color:var(--foreground)]">
                Unit price: <span class="font-semibold">{{ formatNumberNoDecimals(propertyForm.value.price) }} {{ selectedCurrencyCode() }}</span>
            </div>

            <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                <div>
                    <p class="text-sm font-semibold text-[color:var(--foreground)]">Plan flow</p>
                    <p class="text-sm text-[color:var(--muted-foreground)]">Use the old inline flow again: author plans manually or generate dynamic plans from scenarios.</p>
                </div>

                <div class="grid gap-3 sm:grid-cols-3">
                    <button type="button" (click)="setPlanMode('manual')" class="rounded-xl border px-4 py-3 text-left transition-colors" [ngClass]="planMode === 'manual' ? 'border-[color:var(--primary)] bg-[color:var(--primary)]/10 text-[color:var(--foreground)]' : 'border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'">
                        <p class="text-sm font-semibold">Manual</p>
                        <p class="mt-1 text-xs">Enter the plan rows yourself.</p>
                    </button>
                    <button type="button" (click)="setPlanMode('auto')" class="rounded-xl border px-4 py-3 text-left transition-colors" [ngClass]="planMode === 'auto' ? 'border-[color:var(--primary)] bg-[color:var(--primary)]/10 text-[color:var(--foreground)]' : 'border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'">
                        <p class="text-sm font-semibold">Dynamic</p>
                        <p class="mt-1 text-xs">Generate plans from years, cycle, and percentages.</p>
                    </button>
                    <button type="button" (click)="setPlanMode('none')" class="rounded-xl border px-4 py-3 text-left transition-colors" [ngClass]="planMode === 'none' ? 'border-[color:var(--primary)] bg-[color:var(--primary)]/10 text-[color:var(--foreground)]' : 'border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]'">
                        <p class="text-sm font-semibold">No plan</p>
                        <p class="mt-1 text-xs">Leave this unit without inline plans for now.</p>
                    </button>
                </div>
            </div>

            <div *ngIf="planMode === 'auto'" [formGroup]="planGeneratorForm" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm font-semibold text-[color:var(--foreground)]">Dynamic plan scenarios</p>
                        <p class="text-xs text-[color:var(--muted-foreground)]">Generate one or more payment-plan options from the unit price.</p>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <button type="button" (click)="addPlanScenarioPreset(3)" class="rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]">+ 3 years</button>
                        <button type="button" (click)="addPlanScenarioPreset(5)" class="rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]">+ 5 years</button>
                        <button type="button" (click)="addPlanScenario()" class="rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]">Add scenario</button>
                    </div>
                </div>

                <div formArrayName="scenarios" class="space-y-3">
                    <div *ngFor="let scenario of planScenarios.controls; let i = index" [formGroupName]="i" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--background)] p-4">
                        <div class="grid gap-3 md:grid-cols-4">
                            <label class="block space-y-1.5">
                                <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Years</span>
                                <input formControlName="years" type="number" min="1" class="field" />
                            </label>
                            <label class="block space-y-1.5">
                                <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Cycle</span>
                                <select formControlName="paymentFrequency" class="field">
                                    <option value="monthly">Monthly</option>
                                    <option value="Quarterly">Quarterly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </label>
                            <label class="block space-y-1.5">
                                <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Down %</span>
                                <input formControlName="downPercent" type="number" min="0" max="100" class="field" />
                            </label>
                            <label class="block space-y-1.5">
                                <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Reservation %</span>
                                <input formControlName="reservationPercent" type="number" min="0" max="100" class="field" />
                            </label>
                        </div>
                        <div class="mt-3 flex items-center justify-between gap-3">
                            <p class="text-xs text-[color:var(--muted-foreground)]">{{ planScenarioPreview(i) }}</p>
                            <button type="button" (click)="removePlanScenario(i)" [disabled]="planScenarios.length <= 1" class="rounded-lg border border-[color:var(--destructive)]/40 px-3 py-1.5 text-xs font-semibold text-[color:var(--destructive)] hover:bg-[color:var(--destructive)]/10 disabled:opacity-50">Remove</button>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button type="button" (click)="generateAutoPlan()" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary-hover)]">
                        <lucide-icon name="sparkles" class="h-4 w-4"></lucide-icon>
                        Generate dynamic plans
                    </button>
                </div>
            </div>

            <div *ngIf="planMode === 'none'" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 text-sm text-[color:var(--muted-foreground)]">
                No inline payment plans will be created for this unit.
            </div>

            <div *ngIf="planMode !== 'none'" formArrayName="plans" class="space-y-4">
                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-3">
                    <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div class="space-y-1">
                            <p class="text-sm font-semibold text-[color:var(--foreground)]">Public property price</p>
                            <p class="text-xs text-[color:var(--muted-foreground)]">Choose whether the public listing uses the static unit price or one of the payment plan display prices.</p>
                        </div>
                        <label class="block space-y-1.5 lg:min-w-[22rem]">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Public listing price source</span>
                            <select
                                [ngModel]="publicPriceSource"
                                (ngModelChange)="setPublicPriceSource($event)"
                                [ngModelOptions]="{ standalone: true }"
                                class="field"
                            >
                                <option *ngFor="let option of publicPriceSourceOptions()" [ngValue]="option.value">{{ option.label }}</option>
                            </select>
                        </label>
                    </div>
                    <p
                        class="text-xs"
                        [ngClass]="publicPriceSourceHasIssue() ? 'font-semibold text-amber-600' : 'text-[color:var(--text-subtle)]'"
                    >
                        {{ publicPriceSourceHelpText() }}
                    </p>
                </div>

                <div *ngFor="let plan of paymentPlans.controls; let i = index" [formGroupName]="i" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 space-y-4">
                    <div class="flex items-center justify-between gap-3">
                        <div>
                            <p class="text-sm font-semibold text-[color:var(--foreground)]">Plan {{ i + 1 }}</p>
                            <p class="text-xs text-[color:var(--muted-foreground)]">{{ planMode === 'auto' ? 'Generated from the dynamic scenario. You can still fine-tune it.' : 'Manual payment plan.' }}</p>
                        </div>
                        <button type="button" (click)="removePaymentPlan(i)" [disabled]="paymentPlans.length <= 1" class="rounded-lg border border-[color:var(--destructive)]/40 px-3 py-1.5 text-xs font-semibold text-[color:var(--destructive)] hover:bg-[color:var(--destructive)]/10 disabled:opacity-50">Remove</button>
                    </div>

                    <div class="grid gap-3 md:grid-cols-3">
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Total period (months)</span>
                            <input formControlName="totalPeriod" type="number" min="1" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Payment cycle</span>
                            <input formControlName="paymentCycle" type="number" min="1" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Installments</span>
                            <input formControlName="numberOfInstallments" type="number" min="1" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Down payment %</span>
                            <input formControlName="downPayment" type="number" min="0" max="100" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Reservation %</span>
                            <input formControlName="reservationAmount" type="number" min="0" max="100" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Public display price</span>
                            <input formControlName="displayPrice" type="number" min="0" class="field" />
                        </label>
                        <label class="block space-y-1.5">
                            <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Installment amount</span>
                            <input formControlName="installmentAmount" type="number" min="0" class="field" />
                        </label>
                    </div>

                    <div class="space-y-3 border-t border-[color:var(--border)] pt-4">
                        <div class="flex items-center justify-between gap-3">
                            <div>
                                <p class="text-sm font-semibold text-[color:var(--foreground)]">Extra payment rows</p>
                                <p class="text-xs text-[color:var(--muted-foreground)]">These rows are used to build the payment terms text.</p>
                            </div>
                            <button type="button" (click)="addPaymentPlanRow(i)" class="rounded-lg border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]">Add row</button>
                        </div>

                        <div class="space-y-2" formArrayName="extraRows">
                            <div *ngFor="let row of paymentPlanRows(i).controls; let rowIndex = index" [formGroupName]="rowIndex" class="grid gap-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--background)] p-3 md:grid-cols-[1.3fr,0.8fr,auto,auto] items-end">
                                <label class="block space-y-1.5">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Label</span>
                                    <input formControlName="label" type="text" class="field" />
                                </label>
                                <label class="block space-y-1.5">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Percent</span>
                                    <input formControlName="percent" type="number" min="0" max="100" class="field" />
                                </label>
                                <div class="rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-2 text-xs font-semibold text-[color:var(--foreground)]">
                                    {{ paymentPlanRowAmountLabel(i, rowIndex) }}
                                </div>
                                <button type="button" (click)="removePaymentPlanRow(i, rowIndex)" class="rounded-lg border border-[color:var(--destructive)]/40 px-3 py-1.5 text-xs font-semibold text-[color:var(--destructive)] hover:bg-[color:var(--destructive)]/10">Remove</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button type="button" (click)="addPaymentPlan()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-2 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)]">
                        <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
                        Add plan
                    </button>
                </div>
            </div>

            <div class="flex items-center justify-between gap-3">
                <button type="button" (click)="prevStep()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="arrow-left" class="h-4 w-4"></lucide-icon>
                    Back
                </button>
                <button type="button" (click)="nextStep()" [disabled]="saving" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <span>Continue</span>
                    <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
                </button>
            </div>
            <p *ngIf="error" class="text-sm text-[color:var(--destructive)]">{{ error }}</p>
        </form>

        <!-- ═══════════════ Step 6 · Review ═══════════════ -->
        <div *ngSwitchCase="6" class="space-y-6">
            <div class="flex items-center justify-between border-b border-[color:var(--border)] pb-4">
                <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Step 6 of 6</p>
                    <h2 class="mt-1 text-2xl font-bold text-[color:var(--foreground)]">Review &amp; Complete</h2>
                </div>
                <span class="hidden rounded-lg bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-[color:var(--muted-foreground)] sm:inline-flex">Summary</span>
            </div>

            <div *ngIf="validationSummary.length" class="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                <p class="text-sm font-semibold text-[color:var(--destructive)]">Please fix the following before finishing:</p>
                <div class="mt-2 space-y-2">
                    <div *ngFor="let issue of validationSummary" class="flex flex-wrap items-start justify-between gap-3">
                        <p class="text-sm text-[color:var(--destructive)]">
                            Missing fields in Step {{ issue.stepId }} ({{ issue.label }}): {{ issue.fields.join(', ') }}
                        </p>
                        <button
                            type="button"
                            (click)="goToStep(issue.stepId)"
                            class="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-[color:var(--destructive)] transition-colors hover:bg-red-500/20"
                        >
                            Go to step
                        </button>
                    </div>
                </div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Property</p>
                    <p class="mt-1 text-lg font-semibold text-[color:var(--foreground)]">{{ propertyForm.value.title || 'Untitled property' }}</p>
                    <p class="text-sm text-[color:var(--muted-foreground)]">{{ propertyForm.value.tagLine }}</p>
                    <p class="mt-2 text-sm text-[color:var(--foreground)]">Price: {{ (propertyForm.value.price | number:'1.0-0') || 'TBD' }} {{ selectedCurrencyCode() }}</p>
                </div>
                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Payment</p>
                    <p class="mt-1 text-sm text-[color:var(--muted-foreground)]" *ngFor="let plan of paymentPlans.value; let i = index">
                        Plan {{ i + 1 }}: Cycle {{ plan.paymentCycle || '—' }}, Installment {{ (plan.installmentAmount | number:'1.0-0') || '—' }}
                    </p>
                </div>
                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Features</p>
                    <div class="mt-2 flex flex-wrap gap-2">
                        <span *ngFor="let feat of selectedFeatureList()" class="rounded-full bg-[color:var(--primary)]/10 px-3 py-1 text-xs font-semibold text-[color:var(--primary)]">{{ feat }}</span>
                        <span *ngIf="!selectedFeatures.size" class="text-sm text-[color:var(--text-subtle)]">No features selected</span>
                    </div>
                </div>
                <div class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">Landmarks</p>
                    <div class="mt-2 space-y-1" *ngIf="landmarks.value?.length; else noLandmarks">
                        <p *ngFor="let lm of landmarks.value" class="text-sm text-[color:var(--muted-foreground)]">
                            {{ lm.name || 'Unnamed landmark' }}
                            <span *ngIf="lm.distance">· {{ lm.distance }} {{ lm.distanceUnit || 'm' }}</span>
                            <span *ngIf="lm.timeMinutes">· {{ lm.timeMinutes }} min</span>
                        </p>
                    </div>
                    <ng-template #noLandmarks>
                        <p class="mt-2 text-sm text-[color:var(--text-subtle)]">No landmarks added</p>
                    </ng-template>
                </div>
            </div>

            <div class="flex flex-wrap items-center justify-between gap-3">
                <button type="button" (click)="prevStep()" class="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-2 font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
                    <lucide-icon name="arrow-left" class="h-4 w-4"></lucide-icon>
                    Back
                </button>
                <button type="button" (click)="submitAll()" [disabled]="saving" class="inline-flex items-center gap-2 rounded-xl bg-[color:var(--primary)] px-5 py-2 font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)] disabled:opacity-60">
                    <lucide-icon *ngIf="saving" name="loader-2" class="h-4 w-4 animate-spin"></lucide-icon>
                    <lucide-icon *ngIf="!saving" name="check" class="h-4 w-4"></lucide-icon>
                    {{ saving ? 'Creating...' : 'Create property' }}
                </button>
            </div>
        </div>
    </ng-container>
</div>
